import { colourDistanceSquared, type Rgba } from '../core/paint/colour';
import { floodFillPixels } from '../core/paint/floodFill';
import { loadImageUrl } from './imageIO';

/**
 * Flood fill on a layer's PNG.
 *
 * The algorithm lives in `core/paint/floodFill.ts` and is pure; this file only
 * decodes, hands over a pixel buffer and re-encodes. The layer is never
 * mutated in place — the caller commits the result as one history entry.
 */

export interface FillLayerRequest {
  src: string | null;
  width: number;
  height: number;
  x: number;
  y: number;
  colour: Rgba;
  toleranceSquared: number;
}

export type FillLayerResult =
  | { pixels: string; filled: number }
  | { pixels: null; filled: 0; reason: 'outside' | 'already-that-colour' };

export async function fillLayer(request: FillLayerRequest): Promise<FillLayerResult> {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(request.width));
  canvas.height = Math.max(1, Math.round(request.height));
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('This browser cannot render a canvas.');

  if (request.src) {
    const image = await loadImageUrl(request.src);
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  }

  const region = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const result = floodFillPixels({
    data: region.data,
    width: canvas.width,
    height: canvas.height,
    x: request.x,
    y: request.y,
    colour: request.colour,
    toleranceSquared: request.toleranceSquared,
  });

  if ('reason' in result) return { pixels: null, filled: 0, reason: result.reason };
  ctx.putImageData(region, 0, 0);
  return { pixels: canvas.toDataURL('image/png'), filled: result.filled };
}

/**
 * Sample the composited document at a point and return a hex colour, or null
 * if the point is transparent. The eyedropper picks what the user sees, so a
 * transparent hit must say so rather than silently returning black.
 */
export function sampledColour(sample: Rgba | null): string | null {
  if (!sample || sample.alpha < 8) return null;
  const hex = (sample.red << 16) | (sample.green << 8) | sample.blue;
  return `#${hex.toString(16).padStart(6, '0')}`;
}

/** True when a sample is close enough to `colour` to be "the same colour". */
export function sampleMatches(sample: Rgba, colour: Rgba, toleranceSquared: number): boolean {
  return colourDistanceSquared(sample, colour) <= toleranceSquared;
}
